import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { gameStateApi } from '../services/gameStateApi';
import { ACTIVE_SESSION_CONTEXT_KEY } from '../contexts/SessionCommsContext';
import {
    SESSION_SCOPED_CODE,
    SESSION_SCOPED_FLAG,
    normalizeSessionCode,
} from '../utils/sessionGameState';

const LOCAL_TEMP_PREFIX = 'gamenight_temp_';
const SYNCABLE_STATE_KEYS = [
    'uno_game_state',
    'uno_nm_game_state',
    'trio_game_state',
    'charades_game_state',
    'mafia_game_state',
    'mafia_reveal_state',
    'mafia_narrator_state',
    'imposter_setup',
    'imposter_reveal_state',
    'imposter_voting_state',
];

const getComparableTimestamp = (payload, fallbackUpdatedAt) => {
    const savedAt = Number(payload?.savedAt || payload?.timestamp || 0);
    if (Number.isFinite(savedAt) && savedAt > 0) {
        return savedAt;
    }
    const updatedAtMs = new Date(fallbackUpdatedAt || 0).getTime();
    return Number.isFinite(updatedAtMs) ? updatedAtMs : 0;
};

const tryParseJson = (value) => {
    if (typeof value !== 'string' || !value.length) return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const getActiveSessionCodeFromLocalCache = () => {
    const wrapped = tryParseJson(localStorage.getItem(`${LOCAL_TEMP_PREFIX}${ACTIVE_SESSION_CONTEXT_KEY}`));
    return normalizeSessionCode(wrapped?.data?.code);
};

const isSessionScopedStoragePayload = (payload) => Boolean(payload?.data?.[SESSION_SCOPED_FLAG]);

const getSessionScopedStorageCode = (payload) => normalizeSessionCode(payload?.data?.[SESSION_SCOPED_CODE]);

function AccountGameStateSync() {
    const { isAuthenticated, user, isLoading } = useAuth();
    const hasHydratedRef = useRef(false);
    const disabledRef = useRef(false);
    const lastUploadedHashRef = useRef('');

    const activeUserId = String(user?.id || '');

    const syncableStorageKeys = useMemo(
        () => SYNCABLE_STATE_KEYS.map((cacheKey) => `${LOCAL_TEMP_PREFIX}${cacheKey}`),
        []
    );

    useEffect(() => {
        hasHydratedRef.current = false;
        lastUploadedHashRef.current = '';
        disabledRef.current = false;
    }, [activeUserId]);

    useEffect(() => {
        if (isLoading || !isAuthenticated || !activeUserId || disabledRef.current) return undefined;

        const restoreFromAccount = async () => {
            try {
                const data = await gameStateApi.getAll();
                const serverStates = data?.states && typeof data.states === 'object' ? data.states : {};
                const activeSessionCode = getActiveSessionCodeFromLocalCache();

                Object.entries(serverStates).forEach(([cacheKey, wrapper]) => {
                    if (!SYNCABLE_STATE_KEYS.includes(cacheKey)) return;
                    const remotePayload = wrapper?.payload;
                    if (!remotePayload || typeof remotePayload !== 'object') return;

                    const localStorageKey = `${LOCAL_TEMP_PREFIX}${cacheKey}`;
                    let localPayload = tryParseJson(localStorage.getItem(localStorageKey));

                    if (isSessionScopedStoragePayload(localPayload)) {
                        const localSessionCode = getSessionScopedStorageCode(localPayload);
                        if (!activeSessionCode || localSessionCode !== activeSessionCode) {
                            localStorage.removeItem(localStorageKey);
                            localPayload = null;
                        }
                    }

                    if (isSessionScopedStoragePayload(remotePayload)) {
                        const remoteSessionCode = getSessionScopedStorageCode(remotePayload);
                        if (!activeSessionCode || remoteSessionCode !== activeSessionCode) {
                            return;
                        }
                    }

                    const localTs = getComparableTimestamp(localPayload);
                    const remoteTs = getComparableTimestamp(remotePayload, wrapper?.updatedAt);

                    if (!localPayload || remoteTs >= localTs) {
                        localStorage.setItem(localStorageKey, JSON.stringify(remotePayload));
                    }
                });
            } catch {
                // If endpoint/table is unavailable, keep local-only behavior.
                disabledRef.current = true;
            } finally {
                hasHydratedRef.current = true;
            }
        };

        restoreFromAccount().catch(() => {
            disabledRef.current = true;
            hasHydratedRef.current = true;
        });

        return undefined;
    }, [activeUserId, isAuthenticated, isLoading]);

    useEffect(() => {
        if (isLoading || !isAuthenticated || !activeUserId || disabledRef.current) return undefined;

        const syncToAccount = async () => {
            if (!hasHydratedRef.current) return;
            const activeSessionCode = getActiveSessionCodeFromLocalCache();
            const states = {};

            syncableStorageKeys.forEach((localStorageKey, index) => {
                const raw = localStorage.getItem(localStorageKey);
                const parsed = tryParseJson(raw);
                if (!parsed || typeof parsed !== 'object') return;

                if (isSessionScopedStoragePayload(parsed)) {
                    const payloadSessionCode = getSessionScopedStorageCode(parsed);
                    if (!activeSessionCode || payloadSessionCode !== activeSessionCode) {
                        localStorage.removeItem(localStorageKey);
                        return;
                    }
                }

                const cacheKey = SYNCABLE_STATE_KEYS[index];
                if (cacheKey) {
                    states[cacheKey] = parsed;
                }
            });

            const hash = JSON.stringify(states);
            if (hash === lastUploadedHashRef.current) {
                return;
            }

            try {
                await gameStateApi.upsertBulk(states);
                lastUploadedHashRef.current = hash;
            } catch {
                // Avoid spamming failing requests when backend persistence is unavailable.
                disabledRef.current = true;
            }
        };

        syncToAccount().catch(() => {
            disabledRef.current = true;
        });
        const interval = setInterval(() => {
            syncToAccount().catch(() => {
                disabledRef.current = true;
            });
        }, 8000);

        return () => clearInterval(interval);
    }, [activeUserId, isAuthenticated, isLoading, syncableStorageKeys]);

    return null;
}

export default AccountGameStateSync;
